import { useCallback, useState, useEffect } from 'react';
import fetch from '@rd-web-markets/shared/dist/services/Fetcher';
import { request } from '@rd-web-markets/shared/dist/services/request';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import serviceMethods from './service_methods';
import { buildService } from './service';
import { useShowToast } from '../util/toast/useToast';
import { useTranslation } from 'react-i18next';
const api = buildService('/claim_project_reports/{id}/user_access_project_participations', {
  only: ['all', 'create', 'update', 'delete']
});
const userAccessProjectParticipationsService = {
  useParticipations(reportId) {
    const [participations, setParticipations] = useState(null);
    const fetchParticipations = useErrorHandling(useCallback(async () => {
      const participations = await userAccessProjectParticipationsService.all(reportId);
      setParticipations(participations);
    }));
    useEffect(() => {
      fetchParticipations();
    }, [reportId]);
    return [participations, setParticipations];
  },
  useParticipationsByReportIds(reportIds) {
    const [participations, setParticipations] = useState([]);
    const fetchParticipationsByIds = useErrorHandling(useCallback(async () => {
      const participations = await userAccessProjectParticipationsService.allByReportIds(reportIds);
      setParticipations(participations.filter(participation => participation.user_access?.included_in_engagement_team).map(participation => ({
        value: participation.user_access.user.id,
        label: participation.user_access.user.name,
        reportId: participation.claim_project_report_id
      })));
    }, []));
    useEffect(() => {
      fetchParticipationsByIds();
    }, [fetchParticipationsByIds]);
    return participations;
  },
  useDeleteParticipation(reportId, setParticipations) {
    return serviceMethods.useDelete({
      callback: useCallback(async (participation, participations) => {
        await userAccessProjectParticipationsService.delete(reportId, participation.id);
        const updatedParticipations = participations.filter(p => p.id !== participation.id);
        setParticipations([...updatedParticipations]);
      }, [reportId, setParticipations]),
      modalConfirm: {
        type: 'delete',
        confirmationButtonText: participation => participation?.user_access?.user?.name
      }
    });
  },
  useDeleteUserAccessProjectParticipationFromClaimGroup(claimGroup, setClaimGroup, setLoading) {
    return useErrorHandling(useCallback(async (selectedUserAccess, participation, reportId) => {
      setLoading(true);
      await userAccessProjectParticipationsService.delete(reportId, participation.id);
      const userAccessInClaimGroup = claimGroup.user_accesses.find(userAccess => userAccess.id === selectedUserAccess.id);
      userAccessInClaimGroup.user_access_project_participations = userAccessInClaimGroup.user_access_project_participations.filter(p => p.id !== participation.id);
      setClaimGroup({
        ...claimGroup
      });
    }, [claimGroup, setClaimGroup, setLoading]), useCallback(() => {
      setLoading(false);
    }, [setLoading]));
  },
  useSubmitUserAccessProjectParticipation(reportId, setParticipations, setSelectedUserAccess) {
    return useErrorHandling(useCallback(async (selectedUserAccess, participations) => {
      const newParticipation = await userAccessProjectParticipationsService.create(reportId, {
        user_access_id: selectedUserAccess.id ?? selectedUserAccess.user_access_id
      });
      setParticipations([...participations, newParticipation]);
      setSelectedUserAccess(null);
    }, [reportId, setParticipations, setSelectedUserAccess]));
  },
  useCreateUserAccessProjectParticipationFromClaimGroup(claimGroup, setClaimGroup, setLoading) {
    return useErrorHandling(useCallback(async (selectedUserAccess, reportId) => {
      setLoading(true);
      const newParticipation = await userAccessProjectParticipationsService.create(reportId, {
        user_access_id: selectedUserAccess.id
      });
      const userAccessInClaimGroup = claimGroup.user_accesses.find(userAccess => userAccess.id === selectedUserAccess.id);
      userAccessInClaimGroup.user_access_project_participations.push(newParticipation);
      setClaimGroup({
        ...claimGroup
      });
      return userAccessInClaimGroup.user_access_project_participations;
    }, [claimGroup, setClaimGroup, setLoading]), useCallback(() => {
      setLoading(false);
    }, [setLoading]));
  },
  useUpdate(claimGroup, setClaimGroup) {
    const showToast = useShowToast();
    const update = serviceMethods.useUpdate({
      callback: useCallback(async _ref => {
        let {
          user_access_project_participation,
          successMessage
        } = _ref;
        const updated = await api.update(user_access_project_participation.claim_project_report_id, user_access_project_participation.id, {
          user_access_project_participation
        });
        const userAccessInClaimGroup = claimGroup.user_accesses.find(userAccess => userAccess.id === updated.user_access_id);
        const index = userAccessInClaimGroup.user_access_project_participations.findIndex(p => p.id === updated.id);
        userAccessInClaimGroup.user_access_project_participations[index] = updated;
        setClaimGroup({
          ...claimGroup
        });
        if (successMessage) {
          showToast({
            message: successMessage,
            variant: 'success'
          });
        }
        return updated;
      }, [claimGroup, setClaimGroup, showToast])
    });
    return update;
  },
  all(reportId) {
    return fetch(`/api/claim_project_reports/${reportId}/user_access_project_participations`, request.get);
  },
  allByReportIds(reportIds) {
    const query = reportIds.map(reportId => `reportId[]=${reportId}`).join('&');
    return fetch(`/api/claim_project_reports/user_access_projects_participations?${query}`, request.get);
  },
  create(reportId, user_access_project_participation) {
    return fetch(`/api/claim_project_reports/${reportId}/user_access_project_participations/`, {
      ...request.post,
      body: JSON.stringify({
        user_access_project_participation
      })
    });
  },
  delete(reportId, participationId) {
    return fetch(`/api/claim_project_reports/${reportId}/user_access_project_participations/${participationId}`, {
      ...request.delete
    });
  },
  update(reportId, participationId, user_access_project_participation) {
    return fetch(`/api/claim_project_reports/${reportId}/user_access_project_participations/${participationId}`, {
      ...request.put,
      body: JSON.stringify({
        user_access_project_participation
      })
    });
  }
};
export default userAccessProjectParticipationsService;